// Data layer for the Kyvykkyydet capability list — kept out of CapabilityList.tsx so the
// component file only exports a component (React Fast Refresh) and so loadGroups is unit-testable
// (CapabilityList.data.test-style) without a DOM. See #178.
import {
  ApiError,
  getKBBehaviors,
  getKBEquipmentType,
  getZoneKBBehaviors,
  type KBBehavior,
  type KBBehaviorDef,
  type KBStream,
  type ZoneSignal,
} from "@/lib/api/agent";
import { listEquipment, listZones, type Zone } from "@/lib/api/building";
import { streamMembershipRank } from "@/lib/active-components";
import type { CardTone } from "@/lib/card-styles";
import {
  KB_CAPABILITY_HOME_LABEL_FI,
  readinessFromBehaviorStatus,
  type KbCapabilityCardModel,
  type KbCapabilityHome,
  type KbCapabilityLayer,
} from "@/lib/kb-ia";

// Why a group has no behaviors — kept distinct so the UI never conflates the three:
//  - ok           : behaviors loaded (the list may legitimately be empty for a known type)
//  - unknown_type : the KB-behaviors call 404'd — equipment_type is not a KB slug (misconfig)
//  - error        : a transient network/5xx failure — NOT the same as "no behaviors"
export type GroupLoad =
  | { kind: "ok" }
  | { kind: "unknown_type" }
  | { kind: "error"; message: string };

// A 404 means the type is genuinely not in the KB (a diagnosable misconfiguration we must
// surface); anything else is a transient failure the operator should retry, not a silent hide.
function classifyBehaviorsError(err: unknown): GroupLoad {
  if (err instanceof ApiError && err.status === 404)
    return { kind: "unknown_type" };
  return {
    kind: "error",
    message: err instanceof Error ? err.message : "Tuntematon virhe",
  };
}

export interface CapGroup {
  kind: "equipment" | "zone";
  id: string;
  name: string;
  type: string;
  behaviors: KBBehavior[];
  load: GroupLoad; // why behaviors may be empty — drives the per-group reason line
  declared: string[]; // equipment declared_data_points; empty for zones
  variants: string[]; // equipment declared_variants; empty for zones
  signals: ZoneSignal[]; // zone data-point wiring + unlock hints; empty for equipment
  // The type's full behaviour catalog (home/layer/under_ids) — the join source for home-aware
  // grouping (story #926 task #938). Empty for zones and for a load that isn't "ok".
  typeBehaviors: KBBehaviorDef[];
  // The type's components, for resolving a `component`-home behaviour's under_ids to a label.
  typeComponents: { id: string; name_fi: string }[];
  // The type's duct/pipe catalog (equipments/<type>/streams.yaml), [] for a type that declares
  // none — orders component-home sections in process-path sequence (#1157, IA consistency with
  // Rakenne), same `streams[]` as the KB API, no second catalog.
  typeStreams: KBStream[];
  // The type's directly-wireable data point ids (component + shared) — a `required_data_points[]`
  // id absent from this set resolves through the effective-quantity mechanism instead (task #951,
  // post-#914 nuance: docs/spec/knowledge-base.md § Effective-value resolution). Empty for
  // zones and for a load that isn't "ok".
  wireableDataPointIds: string[];
}

// One equipment capability (behaviour) that wiring an as-yet-unwired signal would move toward
// active — the equipment counterpart to `ZoneEnableHint`, built client-side from data already on
// the group (task #951, follow-up to spike #940) rather than a new endpoint.
export interface EquipmentUnlockEntry {
  behaviorId: string;
  behaviorNameFi: string;
  descriptionFi: string;
}

// Reverse index: for every currently-unwired `required_data_points[].id` across the equipment's
// behaviours, which behaviours require it. Mirrors `ZoneEnableHint`'s "what wiring this unlocks"
// framing, scoped to one equipment instance's own capabilities instead of a zone's serving
// equipment (zones get their reverse index from the API; equipment computes it here — no backend
// change, per task #951).
export function buildEquipmentUnlockIndex(
  behaviors: KBBehavior[],
): Map<string, EquipmentUnlockEntry[]> {
  const index = new Map<string, EquipmentUnlockEntry[]>();
  for (const behavior of behaviors) {
    for (const d of behavior.required_data_points) {
      if (d.wired) continue;
      const entries = index.get(d.id) ?? [];
      entries.push({
        behaviorId: behavior.id,
        behaviorNameFi: behavior.name_fi,
        descriptionFi: behavior.description_fi,
      });
      index.set(d.id, entries);
    }
  }
  return index;
}

// Dependencies are injectable so loadGroups can be unit-tested without module mocking.
export interface GroupLoaders {
  listEquipment: typeof listEquipment;
  listZones: typeof listZones;
  getKBBehaviors: typeof getKBBehaviors;
  getZoneKBBehaviors: typeof getZoneKBBehaviors;
  getKBEquipmentType: typeof getKBEquipmentType;
}

const DEFAULT_LOADERS: GroupLoaders = {
  listEquipment,
  listZones,
  getKBBehaviors,
  getZoneKBBehaviors,
  getKBEquipmentType,
};

// Load every equipment and zone as a group. Groups with no behaviors — because the type is
// KB-unknown (404), the type is known but has no behaviors, or the call transiently failed —
// are STILL returned (each carries its `load` reason). Dropping them here is exactly the bug
// that made present equipment vanish from Kyvykkyydet (#178).
export async function loadGroups(
  buildingId: string,
  io: GroupLoaders = DEFAULT_LOADERS,
): Promise<CapGroup[]> {
  const [equipment, zones] = await Promise.all([
    io.listEquipment(buildingId).catch(() => []),
    io.listZones(buildingId).catch((): Zone[] => []),
  ]);
  // The type catalog (home/layer/under_ids + components) is the same for every instance of a
  // type, so fetch it once per distinct equipment_type rather than once per equipment item.
  const typeCache = new Map<
    string,
    Promise<{
      typeBehaviors: KBBehaviorDef[];
      typeComponents: { id: string; name_fi: string }[];
      typeStreams: KBStream[];
      wireableDataPointIds: string[];
    }>
  >();
  function loadTypePlacement(equipmentType: string) {
    let entry = typeCache.get(equipmentType);
    if (!entry) {
      entry = io
        .getKBEquipmentType(equipmentType)
        .then((t) => ({
          typeBehaviors: t.behaviors,
          typeComponents: t.components.map((c) => ({
            id: c.id,
            name_fi: c.name_fi,
          })),
          typeStreams: t.streams ?? [],
          // Directly-wireable ids only — component + shared data points, the same set
          // DataPointMappingPanel treats as declarable (its `present_ids`/`declared` source).
          wireableDataPointIds: [
            ...t.components.flatMap((c) => c.data_points.map((dp) => dp.id)),
            ...(t.shared_data_points ?? []).map((dp) => dp.id),
          ],
        }))
        .catch(() => ({
          typeBehaviors: [],
          typeComponents: [],
          typeStreams: [],
          wireableDataPointIds: [],
        }));
      typeCache.set(equipmentType, entry);
    }
    return entry;
  }

  const eqGroups = await Promise.all(
    equipment.map(async (e): Promise<CapGroup> => {
      let behaviors: KBBehavior[] = [];
      let load: GroupLoad = { kind: "ok" };
      try {
        behaviors = (await io.getKBBehaviors(buildingId, e.id)).behaviors;
      } catch (err) {
        load = classifyBehaviorsError(err);
      }
      const {
        typeBehaviors,
        typeComponents,
        typeStreams,
        wireableDataPointIds,
      } =
        load.kind === "ok"
          ? await loadTypePlacement(e.equipment_type)
          : {
              typeBehaviors: [],
              typeComponents: [],
              typeStreams: [],
              wireableDataPointIds: [],
            };
      return {
        kind: "equipment",
        id: e.id,
        name: e.name,
        type: e.equipment_type,
        behaviors,
        load,
        declared: e.declared_data_points ?? [],
        variants: e.declared_variants ?? [],
        signals: [],
        typeBehaviors,
        typeComponents,
        typeStreams,
        wireableDataPointIds,
      };
    }),
  );
  const zoneGroups = await Promise.all(
    zones.map(async (z): Promise<CapGroup> => {
      let behaviors: KBBehavior[] = [];
      let signals: ZoneSignal[] = [];
      let load: GroupLoad = { kind: "ok" };
      try {
        const response = await io.getZoneKBBehaviors(buildingId, z.id);
        behaviors = response.behaviors;
        signals = response.signals;
      } catch (err) {
        load = classifyBehaviorsError(err);
      }
      return {
        kind: "zone",
        id: z.id,
        name: z.name,
        type: z.zone_type ?? "Vyöhyke",
        behaviors,
        load,
        declared: [],
        variants: [],
        signals,
        typeBehaviors: [],
        typeComponents: [],
        typeStreams: [],
        wireableDataPointIds: [],
      };
    }),
  );
  return [...eqGroups, ...zoneGroups];
}

export interface CapabilityHomeRow {
  def: KBBehaviorDef;
  // The instance's applicable, wired/status-bearing behaviour — present when this equipment can
  // evaluate it. `null` means the type declares the capability but this instance's declared
  // variants/components gate it out: hardware-gated (UC4), not merely blocked, and signal wiring
  // cannot unlock it.
  instance: KBBehavior | null;
}

export interface CapabilityHomeSection {
  home: KbCapabilityHome;
  homeLabel: string;
  // Set only for a `component`-home section scoped to one component (multi-component nesting,
  // e.g. AHU supply/extract fan); null for path/unit/dependency sections.
  componentId: string | null;
  componentLabel: string | null;
  rows: CapabilityHomeRow[];
}

const HOME_ORDER: KbCapabilityHome[] = [
  "component",
  "path",
  "unit",
  "dependency",
];

// Join the instance's applicable behaviours (already variant-filtered by `kb-behaviors`) onto the
// type's full behaviour catalog (`home`/`layer`/`under_ids` — the same fields the Tietopohja type
// view groups by, docs/spec/knowledge-base.md "Capability card contract") so Kyvykkyydet
// groups by KB home too (story #926 task #938's in-plan grouping decision). Frontend-only join —
// no domain change, no second placement catalog: home/layer vocabulary comes from kb-ia.ts.
export function groupBehaviorsByHome(
  instanceBehaviors: KBBehavior[],
  typeBehaviors: KBBehaviorDef[],
  components: { id: string; name_fi: string }[],
  // Type's duct/pipe catalog (#1157, IA consistency) — orders component sections the same way
  // Rakenne orders components, instead of alphabetically. [] (or omitted, e.g. a type with no
  // streams.yaml) keeps the pre-existing alphabetical order.
  streams: KBStream[] = [],
): CapabilityHomeSection[] {
  const instanceById = new Map(instanceBehaviors.map((b) => [b.id, b]));
  const componentLabelById = new Map(components.map((c) => [c.id, c.name_fi]));
  const sections = new Map<string, CapabilityHomeSection>();

  function sectionFor(
    home: KbCapabilityHome,
    componentId: string | null,
  ): CapabilityHomeSection {
    const key = componentId ? `component:${componentId}` : home;
    let section = sections.get(key);
    if (!section) {
      section = {
        home,
        homeLabel: KB_CAPABILITY_HOME_LABEL_FI[home],
        componentId,
        componentLabel: componentId
          ? (componentLabelById.get(componentId) ?? componentId)
          : null,
        rows: [],
      };
      sections.set(key, section);
    }
    return section;
  }

  for (const def of typeBehaviors) {
    // Home is authored on every live behaviour; fall back to "unit" (laite) rather than silently
    // dropping the rare unauthored one.
    const home: KbCapabilityHome = def.home ?? "unit";
    const row: CapabilityHomeRow = {
      def,
      instance: instanceById.get(def.id) ?? null,
    };
    if (home === "component" && def.under_ids && def.under_ids.length > 0) {
      // Multi-component nesting: a capability shared across parts (rare) cross-lists under each.
      for (const componentId of def.under_ids) {
        sectionFor(home, componentId).rows.push(row);
      }
    } else {
      sectionFor(home, null).rows.push(row);
    }
  }

  const streamRank = streamMembershipRank(streams);
  return [...sections.values()].sort((a, b) => {
    const homeDiff = HOME_ORDER.indexOf(a.home) - HOME_ORDER.indexOf(b.home);
    if (homeDiff !== 0) return homeDiff;
    const rankA = a.componentId ? streamRank.get(a.componentId) : undefined;
    const rankB = b.componentId ? streamRank.get(b.componentId) : undefined;
    if (rankA !== undefined && rankB !== undefined && rankA !== rankB) {
      return rankA - rankB;
    }
    if (rankA !== undefined && rankB === undefined) return -1;
    if (rankA === undefined && rankB !== undefined) return 1;
    return (a.componentLabel ?? "").localeCompare(b.componentLabel ?? "", "fi");
  });
}

// Instance KBBehavior → shared Tietopohja capability-card model, with the instance-readiness
// overlay (story #1160 task #1162, extending #1161's `readiness` field) — one card schema at
// every home, instance and type view alike. `signals` stays empty: the
// expandable "Vaaditut signaalit" panel below the card already renders the authoritative
// per-signal wired/unwired detail with the Signaalit deep-link, so a compact id list on the card
// itself would only duplicate it. `status` (the authoring in-kb/proposed/connection chip) is left
// unset — that vocabulary is Tietopohja-only; every live instance behaviour is already "in-kb" by
// definition, so the chip would be redundant noise next to the readiness pill.
export function capabilityCardModelFromInstance(
  behavior: KBBehavior,
  home: KbCapabilityHome,
  layer: KbCapabilityLayer,
): KbCapabilityCardModel {
  return {
    id: behavior.id,
    title_fi: behavior.name_fi,
    layer,
    home,
    description_fi: behavior.description_fi || undefined,
    signals: [],
    points:
      behavior.full_potential_weight > 0
        ? behavior.full_potential_weight
        : null,
    readiness: readinessFromBehaviorStatus(behavior),
  };
}

export interface StatusMeta {
  label: string;
  tone: CardTone;
}

// Honest status vocabulary (docs/spec/concepts.md §2.4 — signal-first unlock): an applicable
// capability reads at a glance as active, blocked by a missing signal, or blocked by a missing
// parameter. A parameter block further distinguishes a config value a human must set ("vaatii
// parametria", warn — actionable now) from a learned parameter still accumulating samples ("oppii",
// passive) via the existing `unready_reason`, rather than re-deriving it from `parameters[]`.
export function statusMeta(
  behavior: Pick<KBBehavior, "status" | "unready_reason">,
): StatusMeta {
  switch (behavior.status) {
    case "active":
      return { label: "aktiivinen", tone: "ok" };
    case "unwired":
      return { label: "vaatii signaalia", tone: "warn" };
    case "unready":
      return behavior.unready_reason === "learning"
        ? { label: "oppii", tone: "learning" }
        : { label: "vaatii parametria", tone: "warn" };
    case "fail":
      return { label: "havainto", tone: "warn" };
    case "no_data":
      return { label: "ei dataa", tone: "warn" };
  }
}
